"use client"

import { useMemo } from "react"
import { sankey, sankeyLinkHorizontal, sankeyJustify } from "d3-sankey"

import type { PopulationSummary } from "@/modules/lernpfad/engine/aggregate"
import { REASON_VAR, SEQUENTIAL_VAR } from "./palette"

/**
 * Where the training budget actually goes: why an hour was planned → how it is
 * delivered.
 *
 * Form choice: this is the one question the rest of the cockpit cannot answer.
 * Coverage says how much is missing, demand says which courses are popular, but
 * neither says whether the programme is mostly compulsory or mostly
 * developmental, nor whether it happens in a room or at a desk. A Sankey is the
 * form built for exactly that — proportional flow between two categorisations —
 * and it re-flows visibly when a policy changes, which is what turns 70-20-10
 * from a percentage into something you can watch happen.
 *
 * `d3-sankey` computes the layout only. The node and link geometry it returns is
 * rendered here against the demo's palette, so this chart shares its type scale,
 * its colours and its hover behaviour with the five hand-rolled ones beside it.
 */

interface SankeyChartProps {
    /** Optional: a summary produced before this field existed has none. */
    flow: PopulationSummary["flow"] | undefined
    originLabel: (origin: string) => string
    modalityLabel: (modality: string) => string
    hoursLabel: (hours: number) => string
    height?: number
}

const WIDTH = 460
const NODE_WIDTH = 10
const NODE_PADDING = 12

/** Origins reuse the reason palette; modalities take the sequential hue. */
const ORIGIN_COLOUR: Record<string, string> = {
    pflicht: REASON_VAR.pflicht,
    regulatorik: REASON_VAR.pflicht,
    voraussetzung: REASON_VAR.voraussetzung,
    rolle: REASON_VAR.luecke,
    mag: REASON_VAR.voraussetzung,
    ziel: REASON_VAR.ziel,
}

export default function SankeyChart({
    flow,
    originLabel,
    modalityLabel,
    hoursLabel,
    height = 200,
}: SankeyChartProps) {
    const layout = useMemo(() => {
        if (!flow || flow.length === 0) return null

        const origins = [...new Set(flow.map((row) => row.origin))]
        const modalities = [...new Set(flow.map((row) => row.modality))]
        const nodes = [
            ...origins.map((id) => ({ id, kind: "origin" as const })),
            ...modalities.map((id) => ({ id, kind: "modality" as const })),
        ]
        const index = new Map(nodes.map((node, position) => [`${node.kind}:${node.id}`, position]))

        const links = flow
            .filter((row) => row.hours > 0)
            .map((row) => ({
                source: index.get(`origin:${row.origin}`)!,
                target: index.get(`modality:${row.modality}`)!,
                value: row.hours,
            }))
        if (links.length === 0) return null

        const generator = sankey<{ id: string; kind: "origin" | "modality" }, { value: number }>()
            .nodeWidth(NODE_WIDTH)
            .nodePadding(NODE_PADDING)
            // `sankeyJustify` pins the sinks to the right edge. The default
            // (`sankeyLeft`) would let a modality that only receives from one
            // origin drift inward, which reads as a third stage that is not there.
            .nodeAlign(sankeyJustify)
            .extent([
                [0, 4],
                [WIDTH, height - 4],
            ])

        // d3-sankey MUTATES the arrays it is given, so they are built fresh on
        // every run rather than reused — passing the same objects twice yields a
        // layout computed from already-laid-out coordinates.
        return generator({
            nodes: nodes.map((node) => ({ ...node })),
            links: links.map((link) => ({ ...link })),
        })
    }, [flow, height])

    if (!layout) return null

    return (
        <figure className="m-0">
            <svg
                viewBox={`0 0 ${WIDTH} ${height}`}
                className="w-full"
                role="img"
                aria-label={(flow ?? [])
                    .map(
                        (row) =>
                            `${originLabel(row.origin)} → ${modalityLabel(row.modality)}: ${hoursLabel(row.hours)}`,
                    )
                    .join(", ")}
            >
                <g>
                    {layout.links.map((link, position) => {
                        const source = link.source as unknown as { id: string; kind: string }
                        return (
                            <path
                                key={position}
                                d={sankeyLinkHorizontal()(link) ?? undefined}
                                fill="none"
                                stroke={ORIGIN_COLOUR[source.id] ?? SEQUENTIAL_VAR}
                                strokeOpacity={0.28}
                                strokeWidth={Math.max(1, link.width ?? 1)}
                            >
                                <title>
                                    {`${originLabel(source.id)} → ${modalityLabel(
                                        (link.target as unknown as { id: string }).id,
                                    )}: ${hoursLabel(link.value)}`}
                                </title>
                            </path>
                        )
                    })}
                </g>

                <g>
                    {layout.nodes.map((node) => {
                        const isOrigin = node.kind === "origin"
                        const label = isOrigin ? originLabel(node.id) : modalityLabel(node.id)
                        return (
                            <g key={`${node.kind}:${node.id}`}>
                                <rect
                                    x={node.x0}
                                    y={node.y0}
                                    width={(node.x1 ?? 0) - (node.x0 ?? 0)}
                                    height={Math.max(1, (node.y1 ?? 0) - (node.y0 ?? 0))}
                                    fill={isOrigin ? (ORIGIN_COLOUR[node.id] ?? SEQUENTIAL_VAR) : SEQUENTIAL_VAR}
                                    rx={2}
                                />
                                <text
                                    // Source labels sit outside on the left, sink
                                    // labels outside on the right, so neither
                                    // overlaps the ribbons in the middle.
                                    x={isOrigin ? (node.x1 ?? 0) + 6 : (node.x0 ?? 0) - 6}
                                    y={((node.y0 ?? 0) + (node.y1 ?? 0)) / 2}
                                    textAnchor={isOrigin ? "start" : "end"}
                                    dominantBaseline="middle"
                                    fontSize={10}
                                    fill="currentColor"
                                    opacity={0.8}
                                >
                                    {label}
                                </text>
                            </g>
                        )
                    })}
                </g>
            </svg>
        </figure>
    )
}
